import React, { createContext, useCallback, useReducer } from 'react';
import api from '../api';

const initialState = {
    classes: [],
    loading: true,
    error: null,
};

// Create Context
export const ClassContext = createContext(initialState);

// Reducer
const classReducer = (state, action) => {
    switch (action.type) {
        case 'GET_CLASS_SUCCESS':
            return {
                ...state,
                currentClass: action.payload,
                loading: false,
            };
        case 'GET_CLASSES_SUCCESS':
            return {
                ...state,
                classes: action.payload,
                loading: false,
            };
        case 'CREATE_CLASS_SUCCESS':
            return {
                ...state,
                classes: [action.payload, ...state.classes],
                loading: false,
            };
        case 'CLASS_ERROR':
            return {
                ...state,
                error: action.payload,
                loading: false,
            };
        case 'GET_STUDENTS_SUCCESS': // New case
            return {
                ...state,
                students: action.payload,
                loading: false,
            };
        case 'ENROLL_STUDENT_SUCCESS': // New case
            return {
                ...state,
                // Update the current class with the new student list
                currentClass: action.payload,
                loading: false,
            };
        case 'GET_DOCUMENTS_SUCCESS': // New case
            return {
                ...state,
                documents: action.payload,
                loading: false,
            };
        case 'UPLOAD_DOCUMENT_SUCCESS': // New case
            return {
                ...state,
                documents: [action.payload.document, ...state.documents],
            };
        case 'DELETE_DOCUMENT_SUCCESS': // New case
            return {
                ...state,
                documents: state.documents.filter(doc => doc._id !== action.payload),
            };
        case 'RENAME_DOCUMENT_SUCCESS': // New case
            return {
                ...state,
                documents: state.documents.map(doc =>
                    doc._id === action.payload._id ? action.payload : doc
                ),
            };
        case 'DELETE_CLASS_SUCCESS': // New case
            return {
                ...state,
                classes: state.classes.filter(cls => cls._id !== action.payload),
            };
        case 'REMOVE_STUDENT_SUCCESS': // New case
            return {
                ...state,
                currentClass: action.payload,
                loading: false,
            };
        default:
            return state;
    }
};

// Provider Component
export const ClassProvider = ({ children }) => {
    const [state, dispatch] = useReducer(classReducer, initialState);

    // Actions
    const getClasses = useCallback(async () => {
        try {
            const res = await api.get('/classes');
            // Handle both old format (array) and new paginated format
            const classesData = res.data.success && res.data.classes
                ? res.data.classes.data
                : res.data;
            dispatch({ type: 'GET_CLASSES_SUCCESS', payload: classesData });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const getAdminClasses = useCallback(async () => {
        try {
            const res = await api.get('/classes/admin');
            // Handle both old format (array) and new paginated format
            const classesData = res.data.success && res.data.classes
                ? res.data.classes.data
                : res.data;
            dispatch({ type: 'GET_CLASSES_SUCCESS', payload: classesData });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const getStudentClasses = useCallback(async () => {
        try {
            const res = await api.get('/classes/student');
            // Handle both old format (array) and new paginated format
            const classesData = res.data.success && res.data.classes
                ? res.data.classes.data
                : res.data;
            dispatch({ type: 'GET_CLASSES_SUCCESS', payload: classesData });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const createClass = useCallback(async (formData) => {
        try {
            const res = await api.post('/classes', formData);
            dispatch({ type: 'CREATE_CLASS_SUCCESS', payload: res.data });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const getClassById = useCallback(async (classId) => {
        try {
            const res = await api.get(`/classes/${classId}`); // Assuming you have a route like this
            dispatch({ type: 'GET_CLASS_SUCCESS', payload: res.data });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const getAllStudents = useCallback(async () => {
        try {
            const res = await api.get('/users/students');
            dispatch({ type: 'GET_STUDENTS_SUCCESS', payload: res.data });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const enrollStudent = useCallback(async (classId, studentId) => {
        try {
            const res = await api.put(`/classes/${classId}/enroll`, { studentId });
            dispatch({ type: 'ENROLL_STUDENT_SUCCESS', payload: res.data });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const getDocumentsForClass = useCallback(async (classId) => {
        try {
            const res = await api.get(`/documents/class/${classId}`);
            // Handle both old format (array) and new paginated format
            const documentsData = res.data.success && res.data.documents
                ? res.data.documents.data
                : res.data;
            dispatch({ type: 'GET_DOCUMENTS_SUCCESS', payload: documentsData });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const uploadDocument = useCallback(async (classId, file) => {
        const formData = new FormData();
        formData.append('document', file); // 'document' must match the backend key

        try {
            const res = await api.post(`/documents/upload/${classId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            dispatch({ type: 'UPLOAD_DOCUMENT_SUCCESS', payload: res.data });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const deleteDocument = useCallback(async (documentId) => {
        try {
            await api.delete(`/documents/${documentId}`);
            dispatch({ type: 'DELETE_DOCUMENT_SUCCESS', payload: documentId });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const renameDocument = useCallback(async (documentId, newFileName) => {
        try {
            const res = await api.put(`/documents/${documentId}/rename`, {
                fileName: newFileName
            });
            dispatch({ type: 'RENAME_DOCUMENT_SUCCESS', payload: res.data.document });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const deleteClass = useCallback(async (classId) => {
        try {
            await api.delete(`/classes/${classId}`);
            dispatch({ type: 'DELETE_CLASS_SUCCESS', payload: classId });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    const removeStudentFromClass = useCallback(async (classId, studentId) => {
        try {
            const res = await api.delete(`/classes/${classId}/students/${studentId}`);
            dispatch({ type: 'REMOVE_STUDENT_SUCCESS', payload: res.data });
        } catch (err) {
            dispatch({ type: 'CLASS_ERROR', payload: err.response.data });
        }
    }, []);

    return (
        <ClassContext.Provider
            value={{
                ...state,
                getClasses,
                getAdminClasses,
                getStudentClasses,
                createClass,
                getClassById,
                getAllStudents,
                enrollStudent,
                getDocumentsForClass,
                uploadDocument,
                deleteDocument,
                renameDocument,
                deleteClass,
                removeStudentFromClass,
            }}
        >
            {children}
        </ClassContext.Provider>
    );
};